// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// Pure-logic helpers for the operations dashboards + notifications. Extracted from the components
// so they can be unit-tested without a DOM (the repo's vitest runs in the `node` environment).

/** Relative "x ago" from an ISO timestamp. now() injectable for deterministic tests. */
export function timeAgo(iso: string, now: number = Date.now()): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (isNaN(t)) return ''
  const s = Math.floor((now - t) / 1000)
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

/** Relative time with "just now" under a minute + date fallback past a day (notifications style). */
export function fmtRelative(iso: string, now: number = Date.now()): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (isNaN(t)) return iso
  const s = Math.floor((now - t) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

/** Ingest-pipeline health summary from mirror status counts. */
export function ingestHealth(mirrorStatus: Record<string, number> | undefined): string {
  const m = mirrorStatus || {}
  const err = m['mirror_error'] || 0
  if (err > 0) return `${err} stuck`
  const ok = m['mirrored'] || 0
  return ok > 0 ? `${ok} mirrored` : 'Idle'
}

/** Top-resource cost footer string from a by_resource cost list. */
export function topCostFoot(byResource: Array<{ resource: string; cost: number }> | undefined): string {
  const r = (byResource || []).filter(x => x.cost > 0).slice(0, 1)[0]
  return r ? `Top: ${r.resource} $${r.cost.toFixed(0)}` : 'No cost data'
}

/** Escape a single CSV cell per RFC-4180 (quote if it contains comma, quote, or newline). */
export function csvCell(value: string | number | null | undefined): string {
  const s = String(value ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Build a CSV string from rows of cells. */
export function toCsv(rows: (string | number)[][]): string {
  return rows.map(r => r.map(csvCell).join(',')).join('\n')
}

/** Map an App Insights trace severityLevel to a label (>=3 error, 2 warning). */
export function severityLabel(level: number): 'error' | 'warning' {
  return level >= 3 ? 'error' : 'warning'
}
